import type { ErrorCopyFor } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, Translation } from "@amtana/i18n/catalog";
import type { MAIL_ERROR_CODES } from "@amtana/mail-contract";

// What staff read when mail refuses a change or a test email (ADR-008). Mail has no browser half,
// so the back office, the only screen that reaches these codes, writes their copy.

const EN = {
  sendFailedTitle: "The provider did not send it",
  sendFailedDescription: "Check the provider's health on this page, then try again.",
  recipientSuppressedTitle: "That address no longer gets email",
  recipientSuppressedDescription: "It bounced or complained before, so the platform stopped sending to it.",
  simulatorInProductionTitle: "The simulator cannot send in production",
  simulatorInProductionDescription: "Choose Amazon SES or Resend, as the primary and as the fallback.",
  senderUnverifiedTitle: "The provider has not verified that domain",
  senderUnverifiedDescription: "Use a from address on a domain the provider verified, then choose it.",
  fallbackSameAsPrimaryTitle: "The fallback is the primary",
  fallbackSameAsPrimaryDescription: "Choose another provider as the fallback, or no fallback.",
  noFallbackTitle: "No fallback is set",
  noFallbackDescription: "Choose a fallback provider under Sending first.",
  eventRefusedTitle: "That provider event was refused",
  eventRefusedDescription: "Its signature or topic did not check out.",
} as const;

type MailErrorCatalog = typeof EN;

const ES: Translation<MailErrorCatalog> = {
  sendFailedTitle: "El proveedor no lo envió",
  sendFailedDescription: "Revisa la salud del proveedor en esta página e inténtalo de nuevo.",
  recipientSuppressedTitle: "Esa dirección ya no recibe correo",
  recipientSuppressedDescription: "Rebotó o se quejó antes, así que la plataforma dejó de enviarle.",
  simulatorInProductionTitle: "El simulador no puede enviar en producción",
  simulatorInProductionDescription: "Elige Amazon SES o Resend, como principal y como respaldo.",
  senderUnverifiedTitle: "El proveedor no ha verificado ese dominio",
  senderUnverifiedDescription:
    "Usa una dirección de remitente en un dominio que el proveedor haya verificado y luego elígelo.",
  fallbackSameAsPrimaryTitle: "El respaldo es el principal",
  fallbackSameAsPrimaryDescription: "Elige otro proveedor como respaldo, o ninguno.",
  noFallbackTitle: "No hay un respaldo definido",
  noFallbackDescription: "Primero elige un proveedor de respaldo en Envío.",
  eventRefusedTitle: "Se rechazó ese evento del proveedor",
  eventRefusedDescription: "Su firma o su tema no se pudo comprobar.",
};

export const MAIL_ERROR_CATALOGS: Catalogs<MailErrorCatalog> = { en: EN, es: ES };

export const MAIL_ERROR_COPY = {
  mail_send_failed: {
    title: phrase(MAIL_ERROR_CATALOGS, "sendFailedTitle"),
    description: phrase(MAIL_ERROR_CATALOGS, "sendFailedDescription"),
  },
  mail_recipient_suppressed: {
    title: phrase(MAIL_ERROR_CATALOGS, "recipientSuppressedTitle"),
    description: phrase(MAIL_ERROR_CATALOGS, "recipientSuppressedDescription"),
  },
  mail_simulator_in_production: {
    title: phrase(MAIL_ERROR_CATALOGS, "simulatorInProductionTitle"),
    description: phrase(MAIL_ERROR_CATALOGS, "simulatorInProductionDescription"),
  },
  mail_sender_unverified: {
    title: phrase(MAIL_ERROR_CATALOGS, "senderUnverifiedTitle"),
    description: phrase(MAIL_ERROR_CATALOGS, "senderUnverifiedDescription"),
  },
  mail_fallback_same_as_primary: {
    title: phrase(MAIL_ERROR_CATALOGS, "fallbackSameAsPrimaryTitle"),
    description: phrase(MAIL_ERROR_CATALOGS, "fallbackSameAsPrimaryDescription"),
  },
  mail_no_fallback: {
    title: phrase(MAIL_ERROR_CATALOGS, "noFallbackTitle"),
    description: phrase(MAIL_ERROR_CATALOGS, "noFallbackDescription"),
  },
  mail_event_refused: {
    title: phrase(MAIL_ERROR_CATALOGS, "eventRefusedTitle"),
    description: phrase(MAIL_ERROR_CATALOGS, "eventRefusedDescription"),
  },
} satisfies ErrorCopyFor<typeof MAIL_ERROR_CODES>;
